import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { HelpCircle, X, Send, Loader2, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Coach({ isAdmin = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `coach-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/coach/chat`,
        {
          message: userMessage,
          session_id: sessionId,
          is_admin: isAdmin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('COACH error:', error);
      const fallbackMessage = isAdmin
        ? "Sorry, I'm unsure as to how I can help you. Please contact Freddie on freddie@scorz.co.uk or on WhatsApp, +44 7796 184248"
        : "Sorry, I'm unsure as to how I can help you. Please contact support@scorz.co.uk";
      setMessages(prev => [...prev, { role: 'assistant', content: fallbackMessage }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/coach/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error clearing session:', error);
    }
    setMessages([]);
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed right-6 bottom-20 z-50 w-14 h-14 rounded-full bg-black text-white shadow-lg hover:bg-gray-800 transition-all flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        aria-label="Open COACH assistant"
        data-testid="coach-open-button"
      >
        <HelpCircle className="w-7 h-7" />
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed right-6 bottom-20 z-50 w-96 max-w-[calc(100vw-3rem)]" data-testid="coach-panel">
          <Card className="flex flex-col h-[500px] max-h-[70vh] shadow-2xl border-2 border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-black text-white px-4 py-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">COACH</h3>
                  <p className="text-xs text-gray-300">AI Assistant</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearChat}
                  className="text-white hover:bg-gray-800 p-2"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-gray-800 p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Hi! I'm COACH</p>
                  <p className="text-sm mt-1">Ask me anything about using Scorz</p>
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => setInputValue('How do I add a gymnast?')}
                      className="block w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors"
                    >
                      How do I add a gymnast?
                    </button>
                    <button
                      onClick={() => setInputValue('How do I enter a competition?')}
                      className="block w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors"
                    >
                      How do I enter a competition?
                    </button>
                    <button
                      onClick={() => setInputValue('How do I upload floor music?')}
                      className="block w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-100 transition-colors"
                    >
                      How do I upload floor music?
                    </button>
                  </div>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-black text-white rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={sendMessage} className="p-3 border-t bg-white shrink-0">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask COACH a question..."
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="coach-input"
                />
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-black hover:bg-gray-800"
                  data-testid="coach-send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}
